import * as React from "react";
import {
  Box,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { HelpTooltip } from "../help/HelpTooltip";
import { FlagIcon } from "../FlagIcon";
import { Controller, useFormContext } from "react-hook-form";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

type BaseProps = Omit<
  React.ComponentProps<typeof TextField>,
  "name" | "select" | "value" | "onChange" | "onBlur" | "ref"
>;

export interface CountrySelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseProps {
  name: TName;
  label?: string;
  required?: boolean;
  tooltip?: string;
  allowedCountryCodes?: Array<CountryOption["code"]>;
}

type CountryOption = {
  code: "AT" | "BE" | "BG" | "HR" | "CY" | "CZ" | "DK" | "EE" | "FI" | "FR" | "DE" | "GR" | "HU" | "IE" | "IT" | "LV" | "LT" | "LU" | "MT" | "NL" | "PL" | "PT" | "RO" | "SK" | "SI" | "ES" | "SE" | "GB";
  label: string;
};

const COUNTRIES: CountryOption[] = [
  // Priority countries
  { code: "PT", label: "Portugal" },
  { code: "GB", label: "United Kingdom" },
  { code: "IT", label: "Italy" },
  { code: "FR", label: "France" },
  { code: "ES", label: "Spain" },

  // Others (alphabetical by country name)
  { code: "AT", label: "Austria" },
  { code: "BE", label: "Belgium" },
  { code: "BG", label: "Bulgaria" },
  { code: "HR", label: "Croatia" },
  { code: "CY", label: "Cyprus" },
  { code: "CZ", label: "Czech Republic" },
  { code: "DK", label: "Denmark" },
  { code: "EE", label: "Estonia" },
  { code: "FI", label: "Finland" },
  { code: "DE", label: "Germany" },
  { code: "GR", label: "Greece" },
  { code: "HU", label: "Hungary" },
  { code: "IE", label: "Ireland" },
  { code: "LV", label: "Latvia" },
  { code: "LT", label: "Lithuania" },
  { code: "LU", label: "Luxembourg" },
  { code: "MT", label: "Malta" },
  { code: "NL", label: "Netherlands" },
  { code: "PL", label: "Poland" },
  { code: "RO", label: "Romania" },
  { code: "SK", label: "Slovakia" },
  { code: "SI", label: "Slovenia" },
  { code: "SE", label: "Sweden" },
];


export function CountrySelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label = "Country",
  required,
  tooltip,
  allowedCountryCodes,
  ...rest
}: CountrySelectProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const { t } = useTranslation();

  const translateError = (m?: string) => (m ? t(m, { defaultValue: m }) : "");

  const availableCountries = React.useMemo(() => {
    if (!allowedCountryCodes || allowedCountryCodes.length === 0) {
      return COUNTRIES;
    }
    return COUNTRIES.filter((c) => allowedCountryCodes.includes(c.code));
  }, [allowedCountryCodes]);

  return (
    <FormControl fullWidth>
      {/* LABEL */}
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        mb={0.5}
        sx={{ pl: 1.2 }}
      >
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#1A1A1A",
          }}
        >
          {label}
        </Typography>

        {required && (
          <Typography sx={{ color: "#E63946", fontWeight: 700 }}>*</Typography>
        )}

        {tooltip && <HelpTooltip title={tooltip} variant="question" />}
      </Box>

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <TextField
              {...rest}
              {...field}
              select
              fullWidth
              error={!!fieldState.error}
              variant="outlined"
              sx={{
                backgroundColor: "#ffffff !important",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  height: 46,
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  paddingLeft: 1.2,
                  "& fieldset": { borderColor: "#d8e0e8" },
                  "&:hover fieldset": { borderColor: "#b9c4d1" },
                  "&.Mui-focused fieldset": { borderColor: "#4b93ff" },
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff !important",
                      color: "#1A1A1A",
                    },
                  },
                },
                renderValue: (value) => {
                  const code =
                    typeof value === "string" ? value : String(value ?? "");
                  const c = COUNTRIES.find((x) => x.code === code);
                  if (!c) return "";

                  return (
                    <Box display="flex" alignItems="center" gap={1.2}>
                      <FlagIcon
                        countryCode={c.code}
                        label={c.label}
                        sx={{ width: "38px", fontSize: "28px" }}
                      />

                      <Box
                        sx={{
                          width: 2,
                          height: 22,
                          bgcolor: "#D9E2EC",
                          marginX: "4px",
                        }}
                      />

                      <Typography
                        sx={{ color: "#1A1A1A", fontSize: "0.92rem" }}
                      >
                        {c.label}
                      </Typography>
                    </Box>
                  );
                },
              }}
            >
              {availableCountries.map((c) => (
                <MenuItem
                  key={c.code}
                  value={c.code}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.92rem",
                  }}
                >
                  <FlagIcon
                    countryCode={c.code}
                    label={c.label}
                    sx={{ width: "38px", fontSize: "28px" }}
                  />
                  {c.label}
                </MenuItem>
              ))}
            </TextField>

            {fieldState.error && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {translateError(fieldState.error.message)}
              </FormHelperText>
            )}
          </>
        )}
      />
    </FormControl>
  );
}
